import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Empty, Input, Select, Space, Tabs, Typography, message } from "antd";
import { PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import { searchInfractionsForPrint } from "../api/infractions/infractionRequest";
import { searchWarningsForPrint } from "../api/warmings/warningRequest";
import { getAgentsForFilter } from "../api/constants";
import VehicleInfractionsTab from "./VehicleDetails/VehicleInfractionsTab";
import VehicleWarningsTab from "./VehicleDetails/VehicleWarningsTab";

const { Title, Text } = Typography;

export default function PrintInfractionsWarnings() {
  const [matricule, setMatricule] = useState("");
  const [searchedMatricule, setSearchedMatricule] = useState("");
  const [infractions, setInfractions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState();
  const searchId = useRef(0);

  useEffect(() => {
    getAgentsForFilter()
      .then(setAgents)
      .catch(() => message.error("Impossible de charger la liste des agents."));
  }, []);

  const handleSearch = async () => {
    const plate = matricule.trim();
    if (!plate && !agentId) {
      message.warning("Saisissez un matricule ou sélectionnez un agent.");
      return;
    }

    const currentSearch = ++searchId.current;
    setLoading(true);
    setSearchedMatricule("");
    setInfractions([]);
    setWarnings([]);
    try {
      const [foundInfractions, foundWarnings] = await Promise.all([
        searchInfractionsForPrint(plate, agentId),
        searchWarningsForPrint(plate, agentId),
      ]);
      if (currentSearch !== searchId.current) return;
      setInfractions(foundInfractions);
      setWarnings(foundWarnings);
      setSearchedMatricule(plate || agents.find((agent) => agent.id_agent === agentId)?.full_name || "Agent");
    } catch (error) {
      if (currentSearch === searchId.current) {
        message.error("Impossible de charger les infractions et les avertissements.");
      }
    } finally {
      if (currentSearch === searchId.current) setLoading(false);
    }
  };

  return (
    <div className="p-5">
      <Card className="mb-5">
        <Space align="start" className="w-full" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <Title level={3}><PrinterOutlined /> Impression des infractions et avertissements</Title>
            <Text type="secondary">Recherchez un matricule pour consulter et imprimer son historique complet.</Text>
          </div>
        </Space>
        <Space wrap style={{ marginTop: 20 }}>
        <Space.Compact style={{ width: 520, maxWidth: "100%" }}>
          <Input
            aria-label="Matricule"
            placeholder="Matricule du véhicule"
            value={matricule}
            maxLength={100}
            onChange={(event) => {
              setMatricule(event.target.value);
              searchId.current += 1;
              setLoading(false);
              setSearchedMatricule("");
            }}
            onPressEnter={handleSearch}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={handleSearch}>
            Rechercher
          </Button>
        </Space.Compact>
        <Select
          showSearch
          allowClear
          placeholder="Filtrer par agent"
          style={{ width: 240 }}
          value={agentId}
          onChange={(value) => {
            setAgentId(value);
            searchId.current += 1;
            setLoading(false);
            setSearchedMatricule("");
          }}
          optionFilterProp="label"
          options={agents.map((agent) => ({ value: agent.id_agent, label: agent.full_name }))}
        />
        </Space>
      </Card>

      {searchedMatricule ? (
        <Card title={`Résultats pour ${searchedMatricule}`}>
          <Tabs
            defaultActiveKey="infractions"
            items={[
              {
                key: "infractions",
                label: `Infractions (${infractions.length})`,
                children: <VehicleInfractionsTab key={searchedMatricule} infractions={infractions} />,
              },
              {
                key: "warnings",
                label: `Avertissements (${warnings.length})`,
                children: <VehicleWarningsTab key={searchedMatricule} warnings={warnings} />,
              },
            ]}
          />
        </Card>
      ) : (
        <Card><Empty description="Saisissez un matricule ou sélectionnez un agent pour afficher les documents." /></Card>
      )}
    </div>
  );
}
